import { useMutation, useQuery } from '@tanstack/react-query';
import {
  getStuckWithdrawalTransactions,
  manuallyConfirmTransaction,
  resetWithdrawalTransaction,
  forceStateChange,
  retryCallback,
  getTransactionDetails
} from '../api/withdrawal';
import { showSuccessNotification, showErrorNotification } from '../utils/notificationUtils';
import { useTranslation } from 'react-i18next';

/**
 * Hook for fetching stuck withdrawal transactions
 * @param options Options for fetching stuck transactions
 * @returns Query result with stuck transactions
 */
export const useStuckWithdrawalTransactions = (options: {
  olderThanMinutes?: number;
  limit?: number;
  status?: string;
  requiresManualIntervention?: boolean;
  enabled?: boolean;
} = {}) => {
  const {
    olderThanMinutes = 30,
    limit = 50,
    status = 'Broadcast',
    requiresManualIntervention = false,
    enabled = true
  } = options;

  return useQuery({
    queryKey: ['stuckWithdrawalTransactions', olderThanMinutes, limit, status, requiresManualIntervention],
    queryFn: () => getStuckWithdrawalTransactions({
      olderThanMinutes,
      limit,
      status,
      requiresManualIntervention
    }),
    enabled,
    refetchInterval: 60000, // Refetch every minute
    select: (data) => {
      if (data.success && Array.isArray(data.data)) {
        return {
          success: true,
          data: data.data,
          count: data.data.length
        };
      }
      return {
        success: false,
        data: [],
        count: 0,
        error: data.error || 'Failed to fetch stuck transactions'
      };
    }
  });
};

/**
 * Hook for fetching transaction details including history
 * @param transactionId Transaction ID
 * @param enabled Whether to enable the query
 * @returns Query result with transaction details
 */
export const useTransactionDetails = (transactionId: string, enabled = false) => {
  return useQuery({
    queryKey: ['transactionDetails', transactionId],
    queryFn: () => getTransactionDetails(transactionId),
    enabled: enabled && !!transactionId,
    select: (data) => {
      if (data.success) {
        return {
          success: true,
          data: data.data
        };
      }
      return {
        success: false,
        data: null,
        error: data.error || 'Failed to fetch transaction details'
      };
    }
  });
};

/**
 * Hook for manually confirming a withdrawal transaction
 * @returns Mutation for manually confirming a transaction
 */
export const useManuallyConfirmTransaction = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (options: { transactionId: string; adminNotes?: string }) =>
      manuallyConfirmTransaction(options),
    onSuccess: (data) => {
      if (data.success) {
        showSuccessNotification(
          t('withdrawal:manualConfirmation.success', 'Transaction Confirmed'),
          t('withdrawal:manualConfirmation.successMessage', 'Transaction has been manually confirmed successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal:manualConfirmation.error', 'Confirmation Failed'),
          data.error || t('withdrawal:manualConfirmation.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:manualConfirmation.error', 'Confirmation Failed'),
        error?.message || t('withdrawal:manualConfirmation.unknownError', 'An unknown error occurred')
      );
    }
  });
};

/**
 * Hook for resetting a withdrawal transaction
 * @returns Mutation for resetting a transaction
 */
export const useResetTransaction = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (options: { transactionId: string; adminNotes?: string }) =>
      resetWithdrawalTransaction(options.transactionId, options.adminNotes),
    onSuccess: (data) => {
      if (data.success) {
        showSuccessNotification(
          t('withdrawal:resetTransaction.success', 'Transaction Reset'),
          t('withdrawal:resetTransaction.successMessage', 'Transaction has been reset successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal:resetTransaction.error', 'Reset Failed'),
          data.error || t('withdrawal:resetTransaction.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:resetTransaction.error', 'Reset Failed'),
        error?.message || t('withdrawal:resetTransaction.unknownError', 'An unknown error occurred')
      );
    }
  });
};

/**
 * Hook for forcing a state change on a withdrawal transaction
 * @returns Mutation for forcing a state change
 */
export const useForceStateChange = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (options: { transactionId: string; targetState: string; adminNotes?: string }) =>
      forceStateChange(options),
    onSuccess: (data) => {
      if (data.success) {
        showSuccessNotification(
          t('withdrawal:forceStateChange.success', 'State Changed'),
          t('withdrawal:forceStateChange.successMessage', 'Transaction state has been changed successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal:forceStateChange.error', 'State Change Failed'),
          data.error || t('withdrawal:forceStateChange.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:forceStateChange.error', 'State Change Failed'),
        error?.message || t('withdrawal:forceStateChange.unknownError', 'An unknown error occurred')
      );
    }
  });
};

/**
 * Hook for retrying a callback for a withdrawal transaction
 * @returns Mutation for retrying a callback
 */
export const useRetryCallback = () => {
  const { t } = useTranslation();

  return useMutation({
    mutationFn: (options: { transactionId: string; adminNotes?: string }) =>
      retryCallback(options),
    onSuccess: (data) => {
      if (data.success) {
        showSuccessNotification(
          t('withdrawal:retryCallback.success', 'Callback Retried'),
          t('withdrawal:retryCallback.successMessage', 'Transaction callback has been retried successfully')
        );
      } else {
        showErrorNotification(
          t('withdrawal:retryCallback.error', 'Retry Failed'),
          data.error || t('withdrawal:retryCallback.unknownError', 'An unknown error occurred')
        );
      }
    },
    onError: (error: any) => {
      showErrorNotification(
        t('withdrawal:retryCallback.error', 'Retry Failed'),
        error?.message || t('withdrawal:retryCallback.unknownError', 'An unknown error occurred')
      );
    }
  });
};
